import { useQuery } from "@tanstack/react-query";
import type { TPractitionerClient, TProfile } from "~/database/types";

import { useApiClient } from "~/hooks/use-api-client";
import { reportQueryError } from "~/lib/query-errors";
import type { UserDetailsData } from "~/modules.server/profile/user-details-service";

export interface Practitioner {
    id: string;
    fullName: string | null;
    email: string | null;
    status: TPractitionerClient["status"];
}

export interface Client {
    id: string;
    fullName: string | null;
    email: string | null;
    displayName?: string;
    status: TPractitionerClient["status"];
}

export const useUserDetails = () => {
    const apiClient = useApiClient();

    return useQuery<UserDetailsData, Error>({
        queryKey: ["user"],
        queryFn: async () => {
            try {
                const response = await apiClient.get("/user");

                if (!response.ok) {
                    if (response.status !== 401) {
                        throw new Error(
                            `Failed to fetch user details: ${response.statusText}`,
                        );
                    }

                    return {
                        profile: undefined as unknown as TProfile,
                        practitioners: [],
                        clients: [],
                        isPractitioner: false,
                        hasPractitioners: false,
                        practitionerEnabledPartImages: false,
                        selfEnabledPartImages: false,
                        practitionerEnabledMeditations: false,
                        selfEnabledMeditations: false,
                        practitionerEnabledCommitments: false,
                        selfEnabledCommitments: false,
                        practitionerLegalLocation: null,
                        practitionerBaaAcceptedAt: null,
                        practitionerBaaNudge: { kind: "hidden" },
                        practitionerDpaPrompt: {
                            show: false,
                            blockedReason: null,
                        },
                        isAdmin: false,
                        isConsultant: false,
                        featureFlags: {},
                        commitmentsCount: 0,
                        showCommitments: false,
                        practitionerEnabledFullSystemMap: false,
                        selfEnabledFullSystemMap: false,
                        showFullSystemMap: false,
                        showMeditations: false,
                        showPartImages: false,
                        billing: null,
                        onboardingChecklist: { eligible: false },
                        practitionerInitialOnboarding: null,
                    };
                }

                return (await response.json()) as UserDetailsData;
            } catch (error) {
                reportQueryError(error, "user details query");
                throw error;
            }
        },
        // Data is prefetched on server via _protected.tsx loader
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        initialDataUpdatedAt: 0,
    });
};
